import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Inbox } from "lucide-react";
import {
  loadAutoOpenQuickAddOnDashboard,
  saveAutoOpenQuickAddOnDashboard,
} from "./inboxQuickAddPrefs";
import { useCreatableCollections } from "./useCreatableCollections";

type QuickAddDashboardSettingProps = {
  userId: string | null;
};

export function QuickAddDashboardSetting({ userId }: QuickAddDashboardSettingProps) {
  const [enabled, setEnabled] = useState(false);
  const { hasCreatableCollections, loading } = useCreatableCollections(Boolean(userId));

  useEffect(() => {
    setEnabled(loadAutoOpenQuickAddOnDashboard(userId));
  }, [userId]);

  if (!userId || loading || !hasCreatableCollections) return null;

  return (
    <Card className="w-full h-full border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/40 shadow-md">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          <Inbox className="w-4 h-4" />
          Quick Add
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <Label htmlFor="auto-open-quick-add-dashboard" className="text-sm font-medium">
              Open Quick Add when visiting the dashboard
            </Label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Shows the capture dialog after login and whenever you return to the dashboard.
              You can also press <kbd className="px-1 py-0.5 rounded border text-[0.65rem]">q</kbd> from
              anywhere.
            </p>
          </div>
          <Switch
            id="auto-open-quick-add-dashboard"
            checked={enabled}
            onCheckedChange={(checked) => {
              setEnabled(checked);
              saveAutoOpenQuickAddOnDashboard(userId, checked);
            }}
            aria-label="Open Quick Add when visiting the dashboard"
          />
        </div>
      </CardContent>
    </Card>
  );
}
